import React, { useState, useCallback } from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaEntry } from '../../../styles/theme';
import type { AppDispatch, RootState } from '../../../redux/store';
import { setLeftoverPortions, type PlannedMeal } from '../../../redux/mealPlanSlice';
import { addLeftoverToFreezerAndSync } from '../../../redux/inventorySlice';
import { shelfLifeExpiryDate } from '../../../services/shelfLifeService';
import { formatDayMonth } from '../../../utils/dateLocale';
import {
  cookDateOf,
  FRIDGE_LEFTOVER_DAYS,
  FREEZER_LEFTOVER_DAYS,
  LEFTOVER_CATEGORY,
} from './leftovers';

const FREEZER_COLOR = '#3a6a8a';
const FREEZER_TINT = '#dde8f0';

interface Props {
  meal: PlannedMeal;
  /** Resolves the dish name for the prompt and the frozen row. */
  title: string;
  onDone?: () => void;
}

function Stepper({
  value, max, onChange, tint, color, testID,
}: {
  value: number; max: number; onChange: (n: number) => void;
  tint: string; color: string; testID: string;
}) {
  return (
    <View style={[styles.stepper, { backgroundColor: tint }]}>
      <Pressable
        testID={`${testID}-minus`}
        style={styles.stepBtn}
        disabled={value <= 0}
        onPress={() => onChange(value - 1)}
      >
        <MaterialCommunityIcons name="minus" size={14} color={value <= 0 ? Paprika.textMuted : color} />
      </Pressable>
      <Text testID={`${testID}-value`} style={[styles.stepValue, { color }]}>{value}</Text>
      <Pressable
        testID={`${testID}-plus`}
        style={styles.stepBtn}
        disabled={value >= max}
        onPress={() => onChange(value + 1)}
      >
        <MaterialCommunityIcons name="plus" size={14} color={value >= max ? Paprika.textMuted : color} />
      </Pressable>
    </View>
  );
}

/**
 * "Des restes ?", asked once per cook.
 *
 * Rendered from planner state rather than wired into `markMealAsCooked`, which
 * is dispatched from five unlike places — DoneView fires it from a bare
 * `useEffect` with no gesture, where a modal could not appear at all. Driving
 * it from the store means those call sites stay untouched and none of them can
 * forget to ask.
 */
export function LeftoverCapturePrompt({ meal, title, onDone }: Props) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const userId = useSelector((s: RootState) => s.auth?.user?.uid as string | undefined);

  const cap = Math.max(1, meal.servings || 1);
  const [fridge, setFridge] = useState(0);
  const [freezer, setFreezer] = useState(0);
  const total = fridge + freezer;

  const cookedOn = cookDateOf(meal);

  const save = useCallback(() => {
    dispatch(setLeftoverPortions({ mealId: meal.id, fridgePortions: fridge }));

    if (freezer > 0) {
      // Built explicitly rather than through the pantry's ingredient pipeline:
      // a dish title is not a shopping-list line, and normalising it would
      // mangle the name and guess a food category for it.
      const name = t('thisWeek.leftoverFrozenName', { recipe: title });
      dispatch(
        addLeftoverToFreezerAndSync({
          id: `leftover-${meal.id}-${Date.now()}`,
          name,
          recipeId: meal.recipeId,
          portions: freezer,
          cookedAt: cookedOn,
          // The shelf-life table already answers 90 days for a name matching
          // "restes"/"leftover", so the freezer date is not invented here.
          expiryDate: shelfLifeExpiryDate({ name, category: LEFTOVER_CATEGORY, location: 'freezer' }),
          userId,
        })
      );
    }
    onDone?.();
  }, [dispatch, meal.id, meal.recipeId, fridge, freezer, title, cookedOn, t, userId, onDone]);

  const dismiss = useCallback(() => {
    dispatch(setLeftoverPortions({ mealId: meal.id, fridgePortions: 0, dismissed: true }));
    onDone?.();
  }, [dispatch, meal.id, onDone]);

  return (
    <View style={styles.card} testID="leftover-capture">
      <View style={styles.header}>
        <View style={styles.icon}>
          <MaterialCommunityIcons name="recycle" size={18} color={PaprikaEntry.leftover.color} />
        </View>
        <View style={styles.headerText}>
          <Text style={styles.title}>{t('thisWeek.leftoverAskTitle', { recipe: title })}</Text>
          <Text style={styles.sub}>
            {t('thisWeek.leftoverAskSub', {
              day: formatDayMonth(cookedOn, i18n.language, 'short'),
              servings: cap,
            })}
          </Text>
        </View>
      </View>

      <View style={[styles.row, { backgroundColor: PaprikaEntry.leftover.tint }]}>
        <MaterialCommunityIcons name="fridge-outline" size={17} color={PaprikaEntry.leftover.color} />
        <View style={styles.rowText}>
          <Text style={[styles.rowTitle, { color: PaprikaEntry.leftover.color }]}>
            {t('thisWeek.leftoverAskFridge')}
          </Text>
          <Text style={styles.rowSub}>
            {t('thisWeek.leftoverAskFridgeSub', { days: FRIDGE_LEFTOVER_DAYS })}
          </Text>
        </View>
        <Stepper
          testID="leftover-fridge"
          value={fridge}
          max={cap - freezer}
          onChange={setFridge}
          tint="rgba(255,255,255,0.65)"
          color={PaprikaEntry.leftover.color}
        />
      </View>

      <View style={[styles.row, { backgroundColor: FREEZER_TINT }]}>
        <MaterialCommunityIcons name="snowflake" size={17} color={FREEZER_COLOR} />
        <View style={styles.rowText}>
          <Text style={[styles.rowTitle, { color: FREEZER_COLOR }]}>
            {t('thisWeek.leftoverAskFreezer')}
          </Text>
          <Text style={styles.rowSub}>
            {t('thisWeek.leftoverAskFreezerSub', { days: FREEZER_LEFTOVER_DAYS })}
          </Text>
        </View>
        <Stepper
          testID="leftover-freezer"
          value={freezer}
          max={cap - fridge}
          onChange={setFreezer}
          tint="rgba(255,255,255,0.65)"
          color={FREEZER_COLOR}
        />
      </View>

      <View style={styles.actions}>
        <Pressable style={styles.ghost} onPress={dismiss} testID="leftover-capture-none">
          <Text style={styles.ghostText}>{t('thisWeek.leftoverAskNone')}</Text>
        </Pressable>
        <Pressable
          style={[styles.primary, total === 0 && styles.primaryOff]}
          disabled={total === 0}
          onPress={save}
          testID="leftover-capture-save"
        >
          <Text style={[styles.primaryText, total === 0 && styles.primaryTextOff]}>
            {t('thisWeek.leftoverAskSave')}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 16,
    padding: 14,
    gap: 8,
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 2,
  },
  header: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 2 },
  icon: {
    width: 32, height: 32, borderRadius: 8,
    backgroundColor: PaprikaEntry.leftover.tint,
    alignItems: 'center', justifyContent: 'center',
  },
  headerText: { flex: 1, gap: 2 },
  title: {
    fontFamily: 'Newsreader-Regular', fontSize: 17, lineHeight: 22,
    color: Paprika.textPrimary,
  },
  sub: { fontFamily: 'Inter-Regular', fontSize: 11.5, color: Paprika.textSecondary },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    borderRadius: 12, paddingHorizontal: 11, paddingVertical: 9,
  },
  rowText: { flex: 1, gap: 1 },
  rowTitle: { fontFamily: 'Inter-Medium', fontSize: 12.5 },
  rowSub: { fontFamily: 'Inter-Regular', fontSize: 10.5, color: Paprika.textSecondary },
  stepper: { flexDirection: 'row', alignItems: 'center', borderRadius: 18, padding: 2 },
  stepBtn: { width: 26, height: 24, alignItems: 'center', justifyContent: 'center' },
  stepValue: {
    fontFamily: 'Inter-SemiBold', fontSize: 12.5,
    width: 26, textAlign: 'center',
  },
  actions: { flexDirection: 'row', gap: 8, marginTop: 4 },
  ghost: {
    flex: 1, paddingVertical: 10, borderRadius: 22,
    borderWidth: 1.5, borderColor: 'rgba(215, 194, 184, 0.5)',
    alignItems: 'center', justifyContent: 'center',
  },
  ghostText: { fontFamily: 'Inter-Medium', fontSize: 13, color: Paprika.textSecondary },
  primary: {
    flex: 1, paddingVertical: 10, borderRadius: 22,
    backgroundColor: Paprika.primary,
    alignItems: 'center', justifyContent: 'center',
  },
  primaryOff: { backgroundColor: Paprika.surfaceHighest },
  primaryText: { fontFamily: 'Inter-Medium', fontSize: 13, color: Paprika.onPrimary },
  primaryTextOff: { color: Paprika.textMuted },
});
